import { useRefCallback } from "@enfusion-ui/hooks";
import { getSelectOption } from "@enfusion-ui/utils";
import { Button, Select, TextInput } from "@enfusion-ui/web-components";
import { styled } from "@enfusion-ui/web-core";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";

import {
  GroupPredicate,
  InspectorReportConfig,
  Predicate,
  PrefilterExpression,
  useInspectorContext,
} from "../../context/inspector/context";

const Container = styled.div<{ noMargin: boolean }>`
  display: flex;
  width: 100%;
  margin-bottom: ${({ noMargin }) => (noMargin ? "0px" : "var(--spacing)")};
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--spacing);
`;

const BtnWrapper = styled.div`
  width: 38px;
  margin: auto 0 0;
`;
const MethodsWrapper = styled.div`
  width: 200px;
  margin-right: var(--spacing);
  display: flex;
  > :first-child {
    margin-right: var(--spacing);
  }
`;

const StyledButton = styled(Button)`
  height: 38px;
  min-width: 36px;
`;

const InputWrapper = styled.div`
  flex: 1;
`;

const PREDICATE_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Any", value: "Any" },
];

const GroupPredicatesOptions = [
  { label: "And", value: "And" },
  { label: "Or", value: "Or" },
];

const expressionOperators = [
  { label: "Is", value: "is" },
  { label: "Is not", value: "isNot" },
  { label: "Contains", value: "contains" },
  { label: "Does not contain", value: "doesNotContain" },
  { label: "Starts with", value: "startsWith" },
  { label: "Ends with", value: "endsWith" },
  { label: "Between", value: "between" },
  { label: "More than", value: "moreThan" },
  { label: "Less than", value: "lessThan" },
  { label: "After", value: "after" },
  { label: "Before", value: "before" },
  { label: "On", value: "on" },
  { label: "Has value", value: "hasValue" },
  { label: "Does not have value", value: "doesNotHaveValue" },
  { label: "Is boolean", value: "isBoolean" },
  { label: "Not between", value: "notBetween" },
  { label: "Date between", value: "dateBetween" },
  { label: "Date not between", value: "dateNotBetween" },
  { label: "Regex contains", value: "regexContains" },
  { label: "Regex does not contain", value: "regexDoesNotContain" },
];

const compoundOperators = [
  "between",
  "notBetween",
  "dateBetween",
  "dateNotBetween",
];
export type ValuesInputProps = {
  defaultValues: Array<PrefilterExpression>;
  max?: number;
  onChange: (newFilter: Array<PrefilterExpression>) => void;
  clearable?: boolean;
};

export const PreFilterReports: React.FC<ValuesInputProps> = ({
  max = 5,
  clearable,
}) => {
  const { config: configBase, setConfig } = useInspectorContext();
  const config = configBase as InspectorReportConfig;
  const [valueState, setValueState] = React.useState<
    Array<PrefilterExpression>
  >(
    config.prefilterExpressions.map((i) => ({
      columnIdentifier: i?.columnIdentifier,
      operator: i?.operator,
      operand1: i?.operand1,
      operand2: i?.operand2,
    }))
  );

  const removeEntry = useRefCallback(
    (idx: number) => {
      return () => {
        setValueState((state) => {
          const newState = [...state];
          const removedState = newState.filter((item, index) => index != idx);
          setConfig({ ...config, prefilterExpressions: removedState });
          return removedState;
        });
      };
    },
    [config]
  );
  const addEntry = useRefCallback(() => {
    setValueState((state) => [
      ...state,
      { columnIdentifier: "", operator: "", operand1: "", operand2: null },
    ]);
  }, []);

  const changeEntryColIdentifier = useRefCallback(
    (idx: number) => {
      return (e: React.ChangeEvent<HTMLInputElement>) => {
        setValueState((state) => {
          const newState = [...state];
          const row = newState[idx];
          if (row !== null) {
            newState[idx] = {
              ...row,
              columnIdentifier: e.target.value.toString(),
            };
          }
          setConfig({ ...config, prefilterExpressions: newState });
          return newState;
        });
      };
    },
    [config]
  );

  const changeEntryOperator = useRefCallback(
    (idx: number) => (e: { label: string; value: string } | null) => {
      setValueState((state) => {
        const newState = [...state];
        const row = newState[idx];
        if (row !== null && e != null) {
          newState[idx] = { ...row, operator: e.value.toString() };
        }
        setConfig({ ...config, prefilterExpressions: newState });
        return newState;
      });
    },
    [config]
  );

  const changeEntryOperand1 = useRefCallback(
    (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setValueState((state) => {
        const newState = [...state];
        const row = newState[idx];
        if (row !== null) {
          newState[idx] = { ...row, operand1: e.target.value.toString() };
        }
        setConfig({ ...config, prefilterExpressions: newState });
        return newState;
      });
    },
    [config]
  );

  const changeEntryOperand2 = useRefCallback(
    (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setValueState((state) => {
        const newState = [...state];
        const row = newState[idx];
        if (row !== null) {
          newState[idx] = { ...row, operand2: e.target.value.toString() };
        }
        setConfig({ ...config, prefilterExpressions: newState });
        return newState;
      });
    },
    [config]
  );

  const handlePredicateChange = useRefCallback(
    (e: { label: string; value: string } | null) => {
      if (e) {
        setConfig({ ...config, predicate: e.value as Predicate });
      }
    },
    [config]
  );

  const handleGroupPredicateChange = useRefCallback(
    (e: { label: string; value: string } | null) => {
      if (e)
        setConfig({ ...config, groupPredicate: e.value as GroupPredicate });
    },
    [config]
  );
  const nonNullCount = valueState.filter((i) => i !== null).length;
  const canAdd = typeof max === "number" ? nonNullCount < max : true;

  return (
    <>
      <MethodsWrapper>
        <Select
          options={PREDICATE_OPTIONS}
          label="Predicate"
          value={getSelectOption(PREDICATE_OPTIONS, config.predicate)}
          clearable={false}
          onChange={handlePredicateChange}
        />

        <Select
          options={GroupPredicatesOptions}
          label="Group Predicate"
          value={getSelectOption(GroupPredicatesOptions, config.groupPredicate)}
          clearable={false}
          onChange={handleGroupPredicateChange}
        />
      </MethodsWrapper>
      {valueState.map((entry, idx) => {
        if (entry === null) {
          return <div key={`${idx}`} style={{ display: "none" }}></div>;
        }
        const { columnIdentifier, operator, operand1, operand2 } = entry;
        return (
          <>
            <Container
              noMargin={!canAdd && idx === valueState.length - 1}
              key={`${idx}`}
            >
              <InputWrapper>
                <TextInput
                  value={columnIdentifier}
                  onChange={changeEntryColIdentifier(idx)}
                  label={idx === 0 ? "Column identifier" : undefined}
                  hideLabel={idx !== 0}
                  clearable={clearable}
                />
              </InputWrapper>
              <InputWrapper>
                <Select
                  label={idx === 0 ? "Operator" : undefined}
                  value={getSelectOption(expressionOperators, operator)}
                  hideLabel={idx !== 0}
                  clearable={clearable}
                  options={expressionOperators}
                  onChange={changeEntryOperator(idx)}
                />
              </InputWrapper>

              <InputWrapper>
                <TextInput
                  value={operand1?.toString()}
                  onChange={changeEntryOperand1(idx)}
                  label={idx === 0 ? "Operand 1" : undefined}
                  hideLabel={idx !== 0}
                  clearable={clearable}
                />
              </InputWrapper>

              <InputWrapper>
                <TextInput
                  value={operand2?.toString()}
                  onChange={changeEntryOperand2(idx)}
                  label={idx === 0 ? "Operand 2" : undefined}
                  hideLabel={idx !== 0}
                  clearable={clearable}
                  disabled={!compoundOperators.includes(entry.operator)}
                />
              </InputWrapper>

              <BtnWrapper>
                <StyledButton name="remove" onClick={removeEntry(idx)}>
                  <FontAwesomeIcon icon={faTrash} size="xs" />
                </StyledButton>
              </BtnWrapper>
            </Container>
          </>
        );
      })}
      {canAdd && (
        <Button
          onClick={addEntry}
          style={{ marginTop: valueState.length ? "0px" : "10px" }}
        >
          Add
        </Button>
      )}
    </>
  );
};
